import clone from 'lodash/clone';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import xorWith from 'lodash/xorWith';
import { IConstant } from 'types';
import { isDevelopment, isTest } from 'config/environment';
import type { ISelectValue } from 'components/ui';

import { errorToast, successfulToast } from './toast';

/* Получение рандомного целого числа: максимум не включается, минимум включается */
export const getRandomInt = (minNumber: number, maxNumber: number): number => {
  const min = Math.ceil(minNumber);
  const max = Math.floor(maxNumber);

  return Math.floor(Math.random() * (max - min)) + min;
};

/* Генерация UUID с fallback */
export const generateUUID = (): string => {
  // Современный и предпочтительный способ (UUID v4)
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback для очень старых браузеров
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  array[6] = (array[6] & 0x0f) | 0x40; // version 4
  array[8] = (array[8] & 0x3f) | 0x80; // variant RFC4122

  return Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
}

/* Расшифровка константы в человекочитаемый формат (например "Квартплата") */
export const getConstantName = (
  constants: IConstant[],
  value: string
): string =>
  find(
    constants,
    ({ value: constantValue }) =>
      constantValue?.toLowerCase() === value?.toLowerCase()
  )?.text || '';

/* Получение значения константы как в базе (например "rent") */
export const getConstantValue = (constants: IConstant[], text: string) =>
  find(
    constants,
    ({ text: constantText }) =>
      constantText?.toLowerCase() === text?.toLowerCase()
  )?.value || '';

/* Получение списка констант формата для react-select */
export const getFormattedConstants = (constants: IConstant[]) => {
  return [
    ...constants.map((constant) => ({
      value: constant.value,
      label: constant.text,
    })),
  ];
};

export const getFormattedSelectedConstants = (
  selected: string[],
  constants: IConstant[]
) => {
  return getFormattedConstants(constants).filter((constant) =>
    selected.includes(constant.value)
  );
};

export const killNulls = (params: {
  [key: string]: any;
}): { [key: string]: any } => {
  const formattedParams = clone(params);

  for (const key in formattedParams) {
    if (
      formattedParams[key] === null ||
      formattedParams[key] === undefined ||
      formattedParams[key] === ''
    ) {
      delete formattedParams[key];
      continue;
    }

    if (typeof formattedParams[key] === 'object') {
      if (Array.isArray(formattedParams[key])) {
        formattedParams[key] = formattedParams[key]
          .map((item: any) =>
            typeof item === 'object' ? killNulls(item) : item
          )
          .filter(
            (item: any) =>
              item !== null &&
              item !== '' &&
              (typeof item !== 'object' || Object.keys(item).length)
          );

        if (formattedParams[key].length === 0) {
          delete formattedParams[key];
        }
      } else {
        formattedParams[key] = killNulls(formattedParams[key]);

        if (Object.keys(formattedParams[key]).length === 0) {
          delete formattedParams[key];
        }
      }
    }
  }

  return formattedParams;
};

/*Преобразование любого массива объектов в удобный селекту*/
export const convertSelectData = (
  data: { [key: string]: any }[],
  labelName: string,
  valueName: string
): ISelectValue[] => {
  const result: ISelectValue[] = [];
  data.forEach((item: any) => {
    result.push({
      value: item[valueName],
      label: item[labelName],
    });
  });

  return result;
};

//TODO: capitalizeOnlyFirstLetter и capitalizeFirstLetter сейчас нигде не используются, но возможно ещё пригодятся

export const capitalizeOnlyFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

//то же самое, что функция capitalizeOnlyFirstLetter, но не видоизменяет следующие буквы
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

//проверка, являются ли 2 массива идентичными (стоит проверить, насколько глубокая проверка)
export const isArrayEqual = (x: any[], y: any[]) =>
  isEmpty(xorWith(x, y, isEqual));

//валидация электронной почты
// https://stackoverflow.com/questions/46155/how-can-i-validate-an-email-address-in-javascript
export const validateEmail = (email: string): boolean => {
  return !!String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

// Удаление всех null, undefined, пустых строк из объекта
export const prepareParams = (
  obj: any,
  withoutValuesArray: any[],
  keepEmptyArraysForKeys: string[] = []
): any => {
  if (Array.isArray(obj)) {
    return obj
      .map((val) =>
        prepareParams(val, withoutValuesArray, keepEmptyArraysForKeys)
      )
      .filter((val) => val !== undefined && val !== null);
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: any = {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (withoutValuesArray.includes(value)) {
        continue;
      }

      if (typeof value === 'object') {
        const prepared = prepareParams(
          value,
          withoutValuesArray,
          keepEmptyArraysForKeys
        );

        // Если ключ в списке keepEmptyArraysForKeys, оставляем пустой массив/объект
        if (
          (Array.isArray(prepared) &&
            prepared.length === 0 &&
            keepEmptyArraysForKeys.includes(key)) ||
          (Object.keys(prepared || {}).length === 0 &&
            keepEmptyArraysForKeys.includes(key))
        ) {
          result[key] = Array.isArray(value) ? [] : {};
        } else if (
          prepared !== undefined &&
          prepared !== null &&
          !(Array.isArray(prepared) && prepared.length === 0)
        ) {
          result[key] = prepared;
        }
      } else {
        result[key] = value;
      }
    }

    return Object.keys(result).length ? result : undefined;
  }

  return obj;
};

// Открытие заявки в журнале заявки
export const openRequestInNewTab = (requestId: string): void => {
  window.open(`/requests/list/${requestId}`);
};

// Возвращает отформатированное кол-во символов после точки.
// Если символов после точки нет или их меньше заданного значения digitsAfterDot, возвращает исходное значение
// В отличии от .toFixed(num) возвращает число, а не строку
export const formatDigitsAfterDot = (num: number, digitsAfterDot: number) => {
  return (
    Math.trunc(num * Math.pow(10, digitsAfterDot)) /
    Math.pow(10, digitsAfterDot)
  );
};

/**
 * Генерирует уникальный идентификатор на основе текущего времени и случайного числа.
 *
 * @returns {string} Уникальный ID в формате `timestamp-randomNumber`, например, `1707072345678-1234`.
 */
export const generateUniqueID = (): string =>
  `${Date.now()}-${Math.floor(Math.random() * 10000)}`;

export const areObjectsEqualByFields = <T>(a: T, b: T, keys: (keyof T)[]) => {
  return keys.every((key) => a[key] === b[key]);
};

/**
 * Возвращает сабдомен на основе текущего hostname.
 *
 * Варианты:
 *   - В локальной среде (`localhost`, `127.0.0.1`, `0.0.0.0`, `10.100.0.*`) —
 *     возвращается `REACT_APP_SUBDOMAIN`, если задан.
 *   - В окружениях с сабдоменом (`frc.eis24.me`) — возвращается `frc`.
 *   - Для зеркала `my.infraxor.ru` — возвращается `dispatcher`.
 *   - В остальных случаях — также используется `REACT_APP_SUBDOMAIN`.
 *
 * @returns {string} Сабдомен в нижнем регистре
 */
export const resolveSubdomain = (): string => {
  const { hostname } = window.location;
  const envSubdomain = process.env.REACT_APP_SUBDOMAIN?.toLowerCase() || '';

  if (isDevelopment() || isTest()) {
    return envSubdomain;
  }

  if (hostname === 'my.infraxor.ru') {
    return 'dispatcher';
  }

  const parts = hostname.split('.');

  if (parts.length >= 3) {
    return parts[0].toLowerCase();
  }

  return envSubdomain;
};
